import { PortfolioAddOrUpdateModel } from "../../../features/portfolio-info/types/PortfolioAddOrUpdateModel";
import { configurationService } from "../../../appConfigService";
import { PortfolioDto } from "../../../features/portfolio-info/types/PortfolioDto";
import { usePortfolioCommonService } from "./usePortfolioCommonService";
import axios from "axios";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { LocalStorageConstants } from "../../constants/LocalStorageConstants";
import { portfolioCalculationService } from "../../services/portfolio-calculation-service/PortfolioCalculationService";
import { useAuthContext } from "../../../features/authentication/hooks/use-auth/AuthContext";
import { PortfolioModel } from "../../../features/portfolio-info/types/PortfolioModel";
import { ParameterType } from "../../../features/options-table/types/ParameterType";
import { ProfileType } from "../../../features/options-table/types/ProfileType";
import { useNavigate } from "react-router-dom";
import { TableInfoType } from "../../../features/options-table/types/TableInfoType";
import {
    OrderForUpdate,
    PositionForUpdate
} from "../../../screens/positions-and-orders/utils/CustomImpliedVolatilityInfoBuilder";
import { BlackScholesModelCalculationService } from "../../services/BlackScholesModelCalculationService";
import type { MarketDataDto } from "../../types/MarketDataDto";

export function useSelectedPortfolioService() {
    const {
        underlyingAssetDto,
        marketDataDto,
        portfolios,
        setPortfolios,
        selectedPortfolio,
        setSelectedPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolioByName,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        setSelectedComparedPortfolio,
        selectedPortfolioDates,
    } = usePortfolioCommonService();

    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    
    async function createPortfolio(portfolioModel: PortfolioAddOrUpdateModel): Promise<void> {
        if (!marketDataDto) {
            return;
        }

        const currentBasePrice = marketDataDto.UnderlyingPrice!;
        const chartDeltaInPercent = 0.2;
        const offset = Math.round(currentBasePrice * chartDeltaInPercent);
        const calculationCount = 200;
        const calculationStep =   Math.round(offset * 2 / calculationCount);

        const newPortfolioModel: PortfolioModel = {
            id: 0,
            name: portfolioModel.name,
            description: portfolioModel.description,
            marginBuffer: portfolioModel.marginBuffer,
            inceptionDate: portfolioModel.inceptionDate,
            useValuationDate: portfolioModel.useValuationDate,
            valuationDate: portfolioModel.valuationDate,
            underlyingAsset: portfolioMappers.mapUnderlyingAsset(underlyingAssetDto!),
            positions: [],
            portfolioItems: [],
            portfolioBalanceHistory: [],
            portfolioSettings: {
                useOffset: true,
                offset: offset,
                minUnderlyingPrice: Math.round(currentBasePrice - offset),
                maxUnderlyingPrice: Math.round(currentBasePrice + offset),
                useBalanceRangeRestriction: false,
                minBalance: 0,
                maxBalance: 0,
                calculationStep: calculationStep !== 0 ? calculationStep : 100,
                selectedOptionParameterType: ParameterType.Delta,
                selectedTableInfoType: TableInfoType.Orders,
                selectedProfileTypes: [ProfileType.Current, ProfileType.Custom, ProfileType.Expiration],
                selectedCustomDate: new Date(),
                hideCommitedBalance: false,
                commitedBalance: 0,
                showTradeSessionBalanceLine: false,
                tradeSessionBalance: 0,
                showSupportLine: false,
                supportLinePrice: 0,
                showResistanceLine: false,
                resistanceLinePrice: 0,
                useCustomPrice: false,
                customPrice: marketDataDto.UnderlyingPrice ?? 0,
                customPriceStep: 100,
                useComparedPortfolio: false,
                comparedPortfolioId: null,
                showDeltaNeutralPriceLines: true,
                showPortfolioPerformance: false,
                usePortfolioAnnualisedRoi: false,
                showDeltaValues: true,
                balanceStartValueX: null,
                balanceEndValueX: null,
                balanceStartValueY: null,
                balanceEndValueY: null,
                isTripleCustomBalanceEnabled: false,
                isTripleCustomBalanceUsingEqualShifts: false,
                isCustomImpliedVolatilityEnabled: false,
                showClosedPositions: false,
                showLockedOrders: false,
            },
            modifiedAt: null,
        };

        await savePortfolio(newPortfolioModel);
        
        portfolios.push(newPortfolioModel);
        setPortfolios(portfolios);
        setSelectedPortfolio(newPortfolioModel);
        refreshPortfolioProfile(newPortfolioModel);
    }
    
    async function editSelectedPortfolio(portfolioModel: PortfolioAddOrUpdateModel): Promise<void>{
        if (!selectedPortfolio) {
            return;
        }

        selectedPortfolio.name = portfolioModel.name;
        selectedPortfolio.description = portfolioModel.description;
        selectedPortfolio.marginBuffer = portfolioModel.marginBuffer;
        selectedPortfolio.inceptionDate = portfolioModel.inceptionDate;
        selectedPortfolio.useValuationDate = portfolioModel.useValuationDate;
        selectedPortfolio.valuationDate = portfolioModel.valuationDate;
        
        setSelectedPortfolio(selectedPortfolio);
        await saveSelectedPortfolio();
    }

    async function cloneSelectedPortfolio(): Promise<void> {
        if (!marketDataDto || selectedPortfolio === null) {
            return;
        }

        const initialPortfolio = selectedPortfolio;
        const newPortfolioModel = portfolioMappers.mapModelToModel(initialPortfolio);
        
        newPortfolioModel.name = getNewPortfolioName();
        newPortfolioModel.id = 0;
        newPortfolioModel.portfolioItems.map(x => x.externalId = 0);
        
        if (newPortfolioModel.portfolioSettings) {
            newPortfolioModel.portfolioSettings.comparedPortfolioId = initialPortfolio.id;
        }

        await savePortfolio(newPortfolioModel);
        
        portfolios.push(newPortfolioModel);
        setPortfolios(portfolios);
        
        setSelectedPortfolio(newPortfolioModel);
        refreshPortfolioProfile(newPortfolioModel);

        setSelectedComparedPortfolio(initialPortfolio);
        refreshComparedPortfolioProfile(newPortfolioModel, initialPortfolio);
        
        function getNewPortfolioName(): string {
            const template = newPortfolioModel.name += ' - Clone';
            let counter = 0;

            while (portfolios.findIndex(x => x.name === getPortfolioName()) !== -1) {
                counter += 1;
            }

            return getPortfolioName();
            
            function getPortfolioName() : string {
                return counter === 0
                    ? template
                    : template + counter;
            }
        }
    }
    
    async function saveSelectedPortfolio(): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }

        await savePortfolio(selectedPortfolio);
    }

    async function fetchSelectedPortfolio(marketData?: MarketDataDto | null): Promise<PortfolioModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        const effectiveMarketData = marketData ?? marketDataDto;

        if (!selectedPortfolio || selectedPortfolio.id === 0 || !effectiveMarketData) {
            return null;
        }

        let reloadedPortfolio: PortfolioModel | null = null;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<PortfolioDto>(
                `api/portfolios/${selectedPortfolio.id}`,
                {
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}` },
                },
            )
            .then((response) => {
                const mappedPortfolio = portfolioMappers.map(
                    response.data,
                    effectiveMarketData.OptionInfos,
                    effectiveMarketData.UnderlyingPrice,
                );

                reloadedPortfolio = mappedPortfolio;
                setSelectedPortfolio(mappedPortfolio);
                setPortfolios(portfolios.map(portfolio =>
                    portfolio.id === mappedPortfolio.id
                        ? mappedPortfolio
                        : portfolio));
                localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, mappedPortfolio.name);
                refreshPortfolioProfile(mappedPortfolio);

                if (selectedComparedPortfolio !== null) {
                    setComparedPortfolioProfile(comparedPortfolioProfile);
                    refreshComparedPortfolioProfile(mappedPortfolio, selectedComparedPortfolio);
                }
            })
            .catch((error: any) => {
                console.log(error);
            });

        return reloadedPortfolio;
    }

    async function savePortfolio(portfolioModel: PortfolioModel): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        const selectedPortfolioDto = portfolioMappers.mapToDto(portfolioModel);

        selectedPortfolioDto.CurrentBalance = portfolioCalculationService.balance
            .calculateAtCurrent(portfolioModel.portfolioItems, marketDataDto!.UnderlyingPrice!);
        selectedPortfolioDto.ExpirationBalance = portfolioCalculationService.balance
            .calculateAtExpiration(portfolioModel.portfolioItems, marketDataDto!.UnderlyingPrice!);

        if (selectedPortfolioDto.Id === 0) {
            await axios
                .create({
                    baseURL: configurationService.configuration.baseAddress,
                })
                .post<number>(
                    'api/portfolios',
                    selectedPortfolioDto,
                    {
                        headers: {
                            ...headerValues.appJson,
                            Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                        },
                    },
                )
                .then((response) => {
                    portfolioModel.id = response.data
                    setSelectedPortfolio(selectedPortfolio);
                    localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, portfolioModel.name);
                })
                .catch((error: any) => {
                    console.log(error);
                });
        } else {
            await axios
                .create({
                    baseURL: configurationService.configuration.baseAddress,
                })
                .put<PortfolioDto>(
                    'api/portfolios',
                    selectedPortfolioDto,
                    {
                        headers: {
                            ...headerValues.appJson,
                            Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                        },
                    },
                )
                .then((response) => {
                    const updatedPortfolioDto = response.data;

                    updatedPortfolioDto?.PortfolioItems.map(x => {
                        const optionInfoDto = marketDataDto?.OptionInfos.find(optionInfo => optionInfo.Symbol === x.Symbol) ?? null;
                        const markImpliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? null;
                        const riskFreeInterest = optionInfoDto?.RiskFreeInterest ?? BlackScholesModelCalculationService.riskFreeRate;;

                        const { price, delta, gamma, vega, theta } =
                            portfolioMappers.getAssetParameters(x.Type, x.Strike, x.ExpireAt,
                                markImpliedVolatility, riskFreeInterest, marketDataDto!.UnderlyingPrice);

                        x.MarkImpliedVolatility = markImpliedVolatility;
                        x.MarkPrice = price;
                        x.Delta = delta * x.Size;
                        x.Gamma = (gamma ?? 0) * x.Size;
                        x.Vega = (vega ?? 0) * x.Size;
                        x.Theta = (theta ?? 0) * x.Size;
                    });
                    
                    setSelectedPortfolio(portfolioMappers.map(updatedPortfolioDto,
                        marketDataDto?.OptionInfos ?? [], marketDataDto?.UnderlyingPrice ?? null));
                    localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, updatedPortfolioDto.Name);
                })
                .catch((error: any) => {
                    console.log(error);
                });
        }
    }

    async function saveCustomImpliedVolatility(
        positionsForUpdate: PositionForUpdate[],
        ordersForUpdate: OrderForUpdate[],
    ): Promise<void> {
        if (positionsForUpdate.length === 0 && ordersForUpdate.length === 0 ) {
            return;
        }
        
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!selectedPortfolio) {
            return;
        }
        
        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .put(
                `api/portfolios/${selectedPortfolio.id}/custom-iv`,
                {
                    positionsForUpdate,
                    ordersForUpdate,
                },
                {
                    headers: {
                        ...headerValues.appJson,
                        Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                    },
                },
            )
            .catch((error: any) => {
                console.log(error);
            });
    }

    async function deleteSelectedPortfolio(forceDelete: boolean): Promise<void> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }
        
        if (!selectedPortfolio) {
            return;
        }

        const deletedPortfolioIndex = portfolios
            .findIndex(x => x.name === selectedPortfolio.name);
        
        if (selectedPortfolio.id !== 0) {
            await axios
                .create({
                    baseURL: configurationService.configuration.baseAddress,
                })
                .delete(
                    `api/portfolios/${selectedPortfolio.id}`,
                    { 
                        params: { forceDelete: forceDelete},
                        headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`},
                    }
                )
                .then(async (response) => {
                    setPortfolios(portfolios.filter(x => x.name !== selectedPortfolio.name));
                })
                .catch((error: any) => {
                    console.log(error);
                });
        } else {
            setPortfolios(portfolios.filter(x => x.name !== selectedPortfolio.name));
        }

        if (portfolios.length > 0) {
            const selectedPortfolio = portfolios[deletedPortfolioIndex - 1];
            setSelectedPortfolio(selectedPortfolio);
            localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, selectedPortfolio.name);
        }
    }

    return {
        marketDataDto,
        portfolios,
        selectedPortfolio,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioByName,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        saveCustomImpliedVolatility,
    };
}

const headerValues = { appJson: { 'Content-Type': 'application/json' } };
